<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useAlert } from 'dashboard/composables';
import { useStore } from 'dashboard/composables/store';
import Copilot from './Copilot.vue';
import { useMapGetter } from 'dashboard/composables/store';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useWindowSize } from '@vueuse/core';
import { vOnClickOutside } from '@vueuse/components';
import wootConstants from 'dashboard/constants/globals';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';

defineProps({
  conversationInboxType: {
    type: String,
    default: '',
  },
});

const store = useStore();
const { uiSettings, updateUISettings } = useUISettings();
const { width: windowWidth } = useWindowSize();

const currentUser = useMapGetter('getCurrentUser');
const assistants = useMapGetter('pilotAssistants/getRecords');
const uiFlags = useMapGetter('pilotAssistants/getUIFlags');
const inboxAssistant = useMapGetter('getPilotAssistant');
const currentChat = useMapGetter('getSelectedChat');

const isSmallScreen = computed(
  () => windowWidth.value < wootConstants.SMALL_SCREEN_BREAKPOINT
);

const selectedCopilotThreadId = ref(null);
const messages = computed(() =>
  store.getters['pilotCopilotMessages/getMessagesByThreadId'](
    selectedCopilotThreadId.value
  )
);

// 线程列表
const threads = useMapGetter('pilotCopilotThreads/getRecords');
const threadsUIFlags = useMapGetter('pilotCopilotThreads/getUIFlags');
const threadsMeta = useMapGetter('pilotCopilotThreads/getMeta');

// 分页状态
const currentPage = ref(1);
const hasMoreThreads = computed(() => {
  const total = threadsMeta.value?.totalCount || 0;
  return threads.value.length < total;
});

// 加载更多线程
const loadMoreThreads = async () => {
  if (threadsUIFlags.value.fetchingList || !hasMoreThreads.value) return;

  currentPage.value += 1;
  await store.dispatch('pilotCopilotThreads/get', { page: currentPage.value });
};

// 刷新线程列表（回到第一页）
const refreshThreads = async () => {
  currentPage.value = 1;
  await store.dispatch('pilotCopilotThreads/get', { page: 1 });
};

const currentAccountId = useMapGetter('getCurrentAccountId');
const isFeatureEnabledonAccount = useMapGetter(
  'accounts/isFeatureEnabledonAccount'
);

const selectedAssistantId = ref(null);

const activeAssistant = computed(() => {
  const preferredId = uiSettings.value.preferred_pilot_assistant_id;

  // If the user has selected a specific assistant, it takes first preference for Copilot.
  if (preferredId) {
    const preferredAssistant = assistants.value.find(a => a.id === preferredId);
    // Return the preferred assistant if found, otherwise continue to next cases
    if (preferredAssistant) return preferredAssistant;
  }

  // If the above is not available, the assistant connected to the inbox takes preference.
  if (inboxAssistant.value) {
    const inboxMatchedAssistant = assistants.value.find(
      a => a.id === inboxAssistant.value.id
    );
    if (inboxMatchedAssistant) return inboxMatchedAssistant;
  }
  // If neither of the above is available, the first assistant in the account takes preference.
  return assistants.value[0];
});

const closeCopilotPanel = () => {
  if (isSmallScreen.value && uiSettings.value?.is_copilot_panel_open) {
    updateUISettings({
      is_contact_sidebar_open: false,
      is_copilot_panel_open: false,
    });
  }
};

const setAssistant = async assistant => {
  selectedAssistantId.value = assistant.id;
  await updateUISettings({
    preferred_pilot_assistant_id: assistant.id,
  });
};

const shouldShowCopilotPanel = computed(() => {
  const isPilotEnabled = isFeatureEnabledonAccount.value(
    currentAccountId.value,
    FEATURE_FLAGS.PILOT
  );
  const { is_copilot_panel_open: isCopilotPanelOpen } = uiSettings.value;
  return isPilotEnabled && isCopilotPanelOpen && !uiFlags.value.fetchingList;
});

const handleReset = () => {
  selectedCopilotThreadId.value = null;
};

// 选择线程并加载历史消息
const selectThread = async threadId => {
  selectedCopilotThreadId.value = threadId;
  if (threadId) {
    await store.dispatch('pilotCopilotMessages/get', threadId);
  }
};

const sendMessage = async message => {
  // 防止重复提交
  if (threadsUIFlags.value.creatingItem) {
    return;
  }

  try {
    if (selectedCopilotThreadId.value) {
      await store.dispatch('pilotCopilotMessages/create', {
        assistant_id: activeAssistant.value.id,
        conversation_id: currentChat.value?.id,
        threadId: selectedCopilotThreadId.value,
        message,
      });
    } else {
      const response = await store.dispatch('pilotCopilotThreads/create', {
        assistant_id: activeAssistant.value.id,
        conversation_id: currentChat.value?.id,
        message,
      });
      selectedCopilotThreadId.value = response.id;
      // 刷新线程列表
      refreshThreads();
    }
  } catch (error) {
    useAlert(error.message);
  }
};

onMounted(() => {
  store.dispatch('pilotAssistants/get');
  refreshThreads();
});
</script>

<template>
  <div
    v-if="shouldShowCopilotPanel"
    v-on-click-outside="() => closeCopilotPanel()"
    class="bg-n-surface-2 h-full overflow-hidden flex-col fixed top-0 ltr:right-0 rtl:left-0 z-40 w-full max-w-sm transition-transform duration-300 ease-in-out md:static md:w-[320px] md:min-w-[320px] ltr:border-l rtl:border-r border-n-weak 2xl:min-w-[360px] 2xl:w-[360px] shadow-lg md:shadow-none"
    :class="[
      {
        'md:flex': shouldShowCopilotPanel,
        'md:hidden': !shouldShowCopilotPanel,
      },
    ]"
  >
    <Copilot
      :messages="messages"
      :threads="threads"
      :selected-thread-id="selectedCopilotThreadId"
      :support-agent="currentUser"
      :conversation-inbox-type="conversationInboxType"
      :assistants="assistants"
      :active-assistant="activeAssistant"
      :has-more-threads="hasMoreThreads"
      :loading-threads="threadsUIFlags.fetchingList"
      @set-assistant="setAssistant"
      @send-message="sendMessage"
      @reset="handleReset"
      @select-thread="selectThread"
      @load-more-threads="loadMoreThreads"
      @delete-all-threads="refreshThreads"
    />
  </div>
  <template v-else />
</template>
